import type { CSSProperties } from 'react';

/**
 * Simple-mode mobile ergonomics (web / PWA aligned with ~48dp touch targets).
 * Used by MMEC simple shell and slide-out filter FABs.
 */
export const MMEC_SIMPLE_TOUCH_MIN = 48;
export const MMEC_SIMPLE_FAB_SIZE = 48;

/**
 * Wrapper for wide tables in Simple mode: momentum scrolling + explicit horizontal pan
 * so scroll intent is less ambiguous next to row taps (checklist A5).
 */
export const MMEC_SIMPLE_SCROLL_TABLE_WRAP_CLASS =
  'touch-pan-x touch-pan-y [-webkit-overflow-scrolling:touch]';

/** Subtle row / header dividers for flow tables in simple mode (theme-agnostic). */
export function mmecSimpleTableHairline(isDark: boolean): string {
  return isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15, 23, 42, 0.07)';
}

/** Alternating row wash — keeps tables readable without heavy cell grids. */
export function mmecSimpleTableRowStripe(isDark: boolean): string {
  return isDark ? 'rgba(255,255,255,0.035)' : 'rgba(15, 23, 42, 0.035)';
}

/** Inline styles for the floating menu / filter trigger in simple tools. */
export const mmecSimpleFabStyle = (opts: {
  left?: number;
  /** When set, FAB anchors to the right (filters drawer on the right). */
  right?: number;
  top?: number | string;
  primary: string;
  textInverse: string;
  border: string;
}): CSSProperties => ({
  position: 'absolute',
  ...(typeof opts.right === 'number'
    ? {
        right: `max(${opts.right}px, env(safe-area-inset-right, 0px))`,
        left: 'auto',
      }
    : {
        left:
          typeof opts.left === 'number'
            ? `max(${opts.left}px, env(safe-area-inset-left, 0px))`
            : `max(8px, env(safe-area-inset-left, 0px))`,
      }),
  top: opts.top ?? '50%',
  transform: typeof opts.top === 'string' && opts.top === '50%' ? 'translateY(-50%)' : undefined,
  zIndex: 25,
  width: MMEC_SIMPLE_FAB_SIZE,
  height: MMEC_SIMPLE_FAB_SIZE,
  minWidth: MMEC_SIMPLE_FAB_SIZE,
  minHeight: MMEC_SIMPLE_FAB_SIZE,
  borderRadius: 999,
  border: `1px solid ${opts.border}`,
  backgroundColor: opts.primary,
  color: opts.textInverse,
  fontSize: 20,
  lineHeight: 1,
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
});

/** Drawer / modal close control — ≥44px hit area. */
export const mmecSimpleCloseBtnStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  minWidth: MMEC_SIMPLE_TOUCH_MIN,
  minHeight: MMEC_SIMPLE_TOUCH_MIN,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  lineHeight: 1,
  padding: 0,
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
};

/**
 * Flow / scanner tables in simple mode: always at least as wide as the scroll strip (removes dead
 * space on the right when few columns are visible), and still wider than 100% when content needs
 * horizontal scroll. Replaces `w-max min-w-full`, which keeps `width: max-content` and can leave a gap.
 */
export const MMEC_SIMPLE_TABLE_FILL_WIDTH_STYLE: CSSProperties = {
  width: 'max(100%, max-content)',
};
